import React from 'react';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const AgentDetailsModal = ({ agent, onClose, onChangeStatus }) => {
  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-600 bg-green-100',
      inactive: 'text-gray-600 bg-gray-100',
      suspended: 'text-red-600 bg-red-100'
    };
    return colors[status] || colors.inactive;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">פרטי סוכן</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Agent Info Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold mr-4">
                {agent.user?.first_name?.charAt(0)}{agent.user?.last_name?.charAt(0)}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {agent.user?.first_name} {agent.user?.last_name}
                </h3>
                <p className="text-gray-600">{agent.user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 space-x-reverse">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agent.status)}`}>
                {agent.status === 'active' && 'פעיל'}
                {agent.status === 'inactive' && 'לא פעיל'}
                {agent.status === 'suspended' && 'מושעה'}
              </span>
              <span className="text-gray-600 text-sm">מזהה עובד: <span className="font-mono font-semibold">{agent.employee_id}</span></span>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Contact Info */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">פרטי קשר</h4>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-500">טלפון:</span>
                  <p className="text-gray-900">{agent.phone || 'לא צוין'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">אימייל:</span>
                  <p className="text-gray-900">{agent.user?.email}</p>
                </div>
              </div>
            </div>

            {/* Employment Info */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">פרטי תעסוקה</h4>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-500">אזור:</span>
                  <p className="text-gray-900">{agent.region || 'לא צוין'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">עמלה:</span>
                  <p className="text-gray-900">{agent.commission_rate}%</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">תאריך תחילת עבודה:</span>
                  <p className="text-gray-900">
                    {agent.hire_date ? new Date(agent.hire_date).toLocaleDateString('he-IL') : 'לא צוין'}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">סטטיסטיקות</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">חברות משויכות:</span>
                  <span className="font-semibold text-blue-600">{agent.stats?.assigned_companies || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">יעדים פעילים:</span>
                  <span className="font-semibold text-green-600">{agent.stats?.active_targets || 0}</span>
                </div>
              </div>
            </div>

            {/* Manager Info */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">מנהל ישיר</h4>
              {agent.manager ? (
                <div>
                  <p className="text-gray-900 font-medium">
                    {agent.manager.user?.first_name} {agent.manager.user?.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{agent.manager.user?.email}</p>
                </div>
              ) : (
                <p className="text-gray-500">ללא מנהל ישיר</p>
              )}
            </div>
          </div>

          {/* Notes */}
          {agent.notes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-2">הערות</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{agent.notes}</p>
            </div>
          )}

          {/* Status Actions */}
          <div className="border-t pt-4">
            <h4 className="font-semibold text-gray-900 mb-3">שינוי סטטוס</h4>
            <div className="flex space-x-3 space-x-reverse">
              {agent.status !== 'active' && (
                <button
                  onClick={() => {
                    onChangeStatus(agent.id, 'active');
                    onClose();
                  }}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  הפעל
                </button>
              )}
              {agent.status !== 'inactive' && (
                <button
                  onClick={() => {
                    onChangeStatus(agent.id, 'inactive');
                    onClose();
                  }}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  השבת
                </button>
              )}
              {agent.status !== 'suspended' && (
                <button
                  onClick={() => {
                    onChangeStatus(agent.id, 'suspended');
                    onClose();
                  }}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                  השעה
                </button>
              )}
            </div>
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailsModal;

